import { _isNil } from '@/littledash';
import { StringUtils } from '@/utils/String.utils';
import type { Edge } from '@xyflow/react';
import type { GraphCanvasNode, LayoutFn, SiblingComparator } from './GraphCanvas.model';

const LEVEL_HEIGHT = 120;
const SIBLING_WIDTH = 360;

/** Default sibling order: by node id, locale-independent so it is stable across environments. */
const compareById: SiblingComparator<Record<string, unknown>> = (a, b) => StringUtils.compareLexicographic(a.id, b.id);

/**
 * A deterministic top-down tree layout for `GraphCanvasEngine`. Depth (y) is each node's distance
 * from a root (a node with no incoming edge) computed via the edge set; horizontal position (x)
 * comes from `compareSiblings` (defaults to node id).
 *
 * Determinism guarantee: the same set of nodes+edges always produces the same positions, and the
 * relative order of same-level nodes is stable (fixed by `compareSiblings`). Adding or removing a
 * node can change existing nodes' indices and recentres its level — so this is not absolute-position
 * stability. Positions are a pure function of the (nodes, edges, comparator) input, independent of the
 * order edges are supplied.
 *
 * Pure: returns new node objects and does not mutate the inputs. Prefer `treeLayout(compareSiblings)`
 * to bind a domain comparator into a `LayoutFn` for the engine config.
 */
export const layoutTree = <TData extends Record<string, unknown>>(
  nodes: ReadonlyArray<GraphCanvasNode<TData>>,
  edges: ReadonlyArray<Edge>,
  compareSiblings: SiblingComparator<TData> = compareById
): Array<GraphCanvasNode<TData>> => {
  // A node with multiple incoming edges resolves to its lowest-id parent, so depth is a pure function
  // of the edge set — independent of the order edges are supplied (keeps the layout deterministic
  // even when the edge set is not strictly a tree).
  const parentByChild = new Map<string, string>();
  edges.forEach((edge) => {
    const existing = parentByChild.get(edge.target);
    if (_isNil(existing) || edge.source < existing) {
      parentByChild.set(edge.target, edge.source);
    }
  });

  const depthOf = (nodeId: string): number => {
    let depth = 0;
    let current = nodeId;
    const guard = new Set<string>();
    while (parentByChild.has(current) && !guard.has(current)) {
      guard.add(current);
      current = parentByChild.get(current)!;
      depth += 1;
    }
    return depth;
  };

  const levels = nodes.reduce<Map<number, Array<GraphCanvasNode<TData>>>>((acc, node) => {
    const depth = depthOf(node.id);
    const bucket = acc.get(depth);
    if (_isNil(bucket)) {
      acc.set(depth, [node]);
    } else {
      bucket.push(node);
    }
    return acc;
  }, new Map());

  const positionById = new Map<string, { x: number; y: number }>();
  levels.forEach((levelNodes, depth) => {
    const ordered = [...levelNodes].sort(compareSiblings);
    const offset = ((ordered.length - 1) * SIBLING_WIDTH) / 2;
    ordered.forEach((node, index) => {
      positionById.set(node.id, { x: index * SIBLING_WIDTH - offset, y: depth * LEVEL_HEIGHT });
    });
  });

  return nodes.map((node) => ({ ...node, position: positionById.get(node.id) ?? { x: 0, y: 0 } }));
};

/**
 * Binds a sibling comparator into a `LayoutFn` for the engine config, so a consumer can order
 * same-level nodes by a domain field. `treeLayout()` (no arg) orders by node id.
 */
export const treeLayout =
  <TData extends Record<string, unknown>>(compareSiblings?: SiblingComparator<TData>): LayoutFn<TData> =>
  (nodes, edges) =>
    layoutTree(nodes, edges, compareSiblings);
